import { Link, useNavigate } from "@remix-run/react";
import { isCourseFree } from "@repo/shared";
import { ArrowRight, Lock, ShoppingCart, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useAddToServerCart } from "~/api/mutations/useAddToCart";
import { useRemoveFromServerCart } from "~/api/mutations/useRemoveFromCart";
import { useCurrentUser } from "~/api/queries/useCurrentUser";
import { Button } from "~/components/ui/button";
import { useCartStore } from "~/lib/stores/cartStore";
import { cn } from "~/lib/utils";

import type { CartItem } from "~/lib/stores/cartStore";

export type CourseCardActionsProps = {
  course: {
    id: string;
    slug?: string | null;
    title: string;
    thumbnailUrl?: string | null;
    authorName?: string;
    categoryName?: string | null;
    priceInCents: number;
    mercadopagoPriceInCents?: number;
    currency: string;
    mercadopagoProductId?: string | null;
    hasFreeChapters?: boolean;
    isPreinscription?: boolean;
  };
  isEnrolled: boolean;
  isPurchased: boolean;
  /**
   * `card-row` es ADITIVA: sirve la fila de 44px del footer de la tarjeta del catálogo (botones al
   * lado del precio en vez de apilados debajo) sin cambiar una línea de lo que renderizan `card` y
   * `detail`.
   *
   * Por qué no se reescribió `card` directamente: el otro consumidor de esa rama es
   * `FeaturedCourses/FeaturedCourseCard`. Verificado por grep que hoy no está montado (su sección y
   * la de `IAPracticaCoursesSection` están comentadas en `Home.page.tsx:10,14`), pero volverlas a
   * la home es descomentar una línea, y una tarjeta de home no tiene por qué heredar el layout de
   * fila del catálogo.
   */
  variant?: "card" | "card-row" | "detail";
};

export function CourseCardActions({
  course,
  isEnrolled,
  isPurchased,
  variant = "card",
}: CourseCardActionsProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();
  const isLoggedIn = !!currentUser;

  const addItemToStore = useCartStore((state) => state.addItem);
  const removeItemFromStore = useCartStore((state) => state.removeItem);
  const setBuyItem = useCartStore((state) => state.setBuyItem);
  const isInCart = useCartStore((state) => state.isInCart(course.id));
  const openCartSidebar = useCartStore((state) => state.setCartSidebarOpen);
  const { mutateAsync: addToServer } = useAddToServerCart();
  const { mutateAsync: removeFromServer } = useRemoveFromServerCart();

  const isFree = isCourseFree(course);

  const isPaid = !isFree;
  const hasPreview = !!course.hasFreeChapters;

  // Detalle del curso dentro de la app. Se emite por UUID y la propia pantalla canonicaliza
  // al slug, así que el link nunca queda roto aunque el slug cambie.
  const courseUrl = `/course/${course.id}/details`;

  const buildCartItem = (): CartItem => ({
    courseId: course.id,
    slug: course.slug ?? null,
    title: course.title,
    thumbnailUrl: course.thumbnailUrl ?? null,
    authorName: course.authorName ?? "",
    categoryName: course.categoryName ?? null,
    priceInCents: course.priceInCents,
    mercadopagoPriceInCents: course.mercadopagoPriceInCents ?? 0,
    currency: course.currency,
    mercadopagoProductId: course.mercadopagoProductId ?? null,
    hasFreeChapters: course.hasFreeChapters ?? false,
    isPreinscription: course.isPreinscription ?? false,
    addedAt: new Date().toISOString(),
  });

  const handleAddAndCheckout = async (buy?: boolean) => {
    if (buy !== undefined) {
      setBuyItem(course.id, buy);
    }
    if (!isInCart) {
      addItemToStore(buildCartItem());
      if (isLoggedIn) {
        try {
          await addToServer(course.id);
        } catch {
          /* local cart updated */
        }
      }
    }
    navigate("/checkout");
  };

  const handleAddToCart = async (buy?: boolean) => {
    if (buy !== undefined) {
      setBuyItem(course.id, buy);
    }
    addItemToStore(buildCartItem());
    if (isLoggedIn) {
      try {
        await addToServer(course.id);
      } catch {
        /* local cart updated */
      }
    }
  };

  const handleRemoveFromCart = async () => {
    removeItemFromStore(course.id);
    if (isLoggedIn) {
      try {
        await removeFromServer(course.id);
      } catch {
        /* local cart updated */
      }
    }
  };

  // Fila horizontal de 44px (footer de la tarjeta del catálogo) vs columna apilada (card/detail).
  const isRow = variant === "card-row";
  // Todo lo que no es `detail` comparte los colores de CTA de tarjeta.
  const isCardish = variant !== "detail";

  // Brand CTA colors for both variants
  const ctaBrand = isCardish
    ? "bg-landing-red hover:bg-landing-red-hover text-white hover:opacity-100"
    : "bg-landing-red hover:bg-landing-red-hover hover:opacity-100 text-white";
  const outlineCart = isCardish
    ? "hover:bg-landing-red-hover hover:text-white hover:border-landing-red-hover hover:opacity-100"
    : "";

  // ═══ Piezas de la variante `card-row` ═══
  // El contenedor pasa a fila y comparte los 324px útiles con el chip de precio: CTA elástico +
  // botón de carrito de 44×44. `size="lg"` es el que da los 44px del mínimo táctil (el `sm` de
  // antes medía 36 y ninguna medición previa lo había mirado); su `px-8` se pisa con `px-3`.
  const rowWrap = isRow ? "flex min-w-0 flex-1 flex-row items-center gap-2" : "flex flex-col gap-2";
  const btnSize = isRow ? ("lg" as const) : ("sm" as const);
  // `text-[15px]` y `font-semibold`: §10.2 del manual ata el cuerpo del label al alto del botón (a 44
  // le corresponde 15px, no 13) y pide DM Sans 600 para el CTA primario. El primitivo `Button` trae
  // `text-sm font-medium` en su clase base, así que las dos cosas hay que pisarlas por className —
  // tailwind-merge desduplica y no hace falta tocar el primitivo, que lo comparte el admin.
  const primaryCls = isRow ? "min-w-0 flex-1 px-3 text-[15px] font-semibold" : "w-full";
  // `bg-landing-white` porque `brand-outline` trae `bg-white`, que en oscuro cae al negro frío
  // #08080c del admin en vez del marrón cálido de la marca.
  const iconOnlyCls = isRow ? "h-11 w-11 shrink-0 bg-landing-white px-0" : "w-full";
  // El ring del primitivo es `--ring` (gris en claro, azul en oscuro): dentro de una tarjeta de
  // landing el foco va en rojo de marca, con el offset del color de la tarjeta.
  const rowFocus = isRow
    ? "focus-visible:ring-landing-red focus-visible:ring-offset-landing-white"
    : "";
  // En la fila, el label puede no entrar: `truncate` necesita un elemento propio porque el botón es
  // flex y un nodo de texto pelado no se recorta. Fuera de `card-row` no se envuelve nada, para que
  // el render de `card` (y de la home) quede idéntico al de antes.
  const label = (text: string) => (isRow ? <span className="truncate">{text}</span> : text);
  // En `card-row` el botón del carrito es sólo el ícono y se nombra con `aria-label={cartAria}`
  // ("Sumar {título} al carrito"). Antes ADEMÁS metía un `<span class="sr-only">` con el label largo:
  // el `aria-label` gana el nombre accesible, pero el sr-only sigue siendo un nodo de texto hijo, así
  // que recorriendo la página en modo lectura el botón se oía DOS veces. Se queda el `aria-label`,
  // que es el que además nombra QUÉ curso se agrega — el sr-only decía sólo "Agregar al carrito".
  // Fuera de `card-row` no hay `aria-label`, así que ahí el texto sí tiene que estar en el DOM.
  const srLabel = (text: string) => (isRow ? null : text);
  const cartAria = isRow ? t("landing.courses.card.cartAria", { title: course.title }) : undefined;

  // === STATE: Purchased → "Continuar Aprendiendo" ===
  if (isPurchased) {
    if (variant === "detail") {
      return (
        <Button size="lg" variant="brand" className={`w-full ${ctaBrand}`} asChild>
          <Link to={courseUrl}>
            <ArrowRight className="mr-2 size-4" />
            {t("landing.courseDetail.continueLearning")}
          </Link>
        </Button>
      );
    }

    return (
      <Button variant="brand" className={cn(primaryCls, ctaBrand, rowFocus)} size={btnSize} asChild>
        <Link to={courseUrl}>
          <ArrowRight aria-hidden="true" className={isRow ? "mr-1.5 size-4" : "mr-2 size-3.5"} />
          {label(t("landing.courses.card.continue"))}
        </Link>
      </Button>
    );
  }

  // === STATE: Enrolled but NOT purchased (paid course) ===
  if (isEnrolled && !isPurchased) {
    if (isInCart) {
      // Enrolled + in cart → checkout / remove / view cart
      if (variant === "detail") {
        return (
          <div className="flex flex-col gap-2">
            <Button size="lg" variant="brand" className={`w-full ${ctaBrand}`} asChild>
              <Link to="/checkout">{t("cart.button.goToCheckout")}</Link>
            </Button>
            <Button
              size="lg"
              variant="destructive"
              className="w-full"
              onClick={handleRemoveFromCart}
            >
              <Trash2 className="mr-2 size-4" />
              {t("cart.button.removeFromCart")}
            </Button>
          </div>
        );
      }

      return (
        <div className={rowWrap}>
          <Button
            variant="brand"
            className={cn(primaryCls, ctaBrand, rowFocus)}
            size={btnSize}
            asChild
          >
            <Link to="/checkout">{label(t("cart.button.goToCheckout"))}</Link>
          </Button>
          {/* `contents` en la fila: el botón de quitar sube a ser hijo directo del flex del footer,
              sin envoltorio intermedio que le rompa el ancho de 44px. */}
          <div className={isRow ? "contents" : "flex gap-2"}>
            <Button
              variant={isRow ? "brand-outline" : "destructive"}
              size={btnSize}
              className={cn(isRow && iconOnlyCls, rowFocus)}
              onClick={handleRemoveFromCart}
              aria-label={t("cart.button.removeFromCart")}
            >
              <Trash2 aria-hidden="true" className={isRow ? "size-4" : "size-3.5"} />
            </Button>
            {/* "Ver carrito" no entra en la fila de 44px y es la salida menos urgente de las tres:
                el checkout ya está en el primario, quitar es el undo, y el carrito se abre desde el
                header en cualquier pantalla. En `card`/`detail` sigue igual que antes. */}
            {!isRow && (
              <Button
                variant="brand-outline"
                className={`flex-1 ${outlineCart}`}
                size="sm"
                onClick={() => openCartSidebar(true)}
              >
                <ShoppingCart aria-hidden="true" className="mr-1 size-3.5" />
                {t("cart.button.viewCart")}
              </Button>
            )}
          </div>
        </div>
      );
    }

    // Enrolled, not in cart → buy course + add to cart
    if (variant === "detail") {
      return (
        <div className="flex flex-col gap-2">
          <Button
            size="lg"
            variant="brand"
            className={`w-full ${ctaBrand}`}
            onClick={() => handleAddAndCheckout(true)}
          >
            <Lock className="mr-2 size-4" />
            {t("landing.courseDetail.buyCourse")}
          </Button>
          <Button
            size="lg"
            variant="brand-outline"
            className={`w-full ${outlineCart}`}
            onClick={() => handleAddToCart(true)}
          >
            <ShoppingCart className="mr-2 size-4" />
            {t("cart.button.addToCartFull")}
          </Button>
        </div>
      );
    }

    return (
      <div className={rowWrap}>
        <Button
          variant="brand"
          className={cn(primaryCls, ctaBrand, rowFocus)}
          size={btnSize}
          onClick={() => handleAddAndCheckout(true)}
        >
          <Lock aria-hidden="true" className={isRow ? "mr-1.5 size-4" : "mr-1 size-3.5"} />
          {label(t("cart.button.buyCourse"))}
        </Button>
        <Button
          variant="brand-outline"
          className={cn(iconOnlyCls, outlineCart, rowFocus)}
          size={btnSize}
          onClick={() => handleAddToCart(true)}
          aria-label={cartAria}
        >
          <ShoppingCart aria-hidden="true" className={isRow ? "size-4" : "mr-1 size-3.5"} />
          {srLabel(t("cart.button.addToCartFull"))}
        </Button>
      </div>
    );
  }

  // === STATE: Curso GRATIS, no lo tiene todavía ===
  // Toda adquisición pasa por checkout, incluso cuando el total es $0. Esto mantiene un
  // único contrato para login, aceptación de términos, conciliación de cursos ya adquiridos
  // e inscripción final.
  if (isFree && !isInCart) {
    if (variant === "detail") {
      return (
        <div className="flex flex-col gap-2">
          <Button
            size="lg"
            variant="brand"
            className={`w-full ${ctaBrand}`}
            onClick={() => handleAddAndCheckout(true)}
          >
            <ArrowRight className="mr-2 size-4" />
            {t("landing.courseDetail.enrollFree")}
          </Button>
          <Button
            size="lg"
            variant="brand-outline"
            className={`w-full ${outlineCart}`}
            onClick={() => handleAddToCart(true)}
          >
            <ShoppingCart className="mr-2 size-4" />
            {t("cart.button.addToCartFull")}
          </Button>
        </div>
      );
    }

    return (
      <div className={rowWrap}>
        <Button
          variant="brand"
          className={cn(primaryCls, ctaBrand, rowFocus)}
          size={btnSize}
          onClick={() => handleAddAndCheckout(true)}
        >
          {/* El label dice "Inscribirme" y no "Inscribirme Gratis": el chip de precio que está pegado
              a la izquierda de este botón ya dice "Gratis", y la tarjeta lo decía TRES veces (badge +
              chip + CTA). La clave sigue llamándose `enrollFreeShort` porque sigue siendo el CTA del
              curso gratis. */}
          {label(t("cart.button.enrollFreeShort"))}
        </Button>
        <Button
          variant="brand-outline"
          className={cn(iconOnlyCls, outlineCart, rowFocus)}
          size={btnSize}
          onClick={() => handleAddToCart(true)}
          aria-label={cartAria}
        >
          <ShoppingCart aria-hidden="true" className={isRow ? "size-4" : "mr-1 size-3.5"} />
          {srLabel(t("cart.button.addToCartFull"))}
        </Button>
      </div>
    );
  }

  // === STATE: In cart (any course type) ===
  if (isInCart) {
    if (variant === "detail") {
      return (
        <div className="flex flex-col gap-2">
          <Button size="lg" variant="brand" className={`w-full ${ctaBrand}`} asChild>
            <Link to="/checkout">{t("cart.button.goToCheckout")}</Link>
          </Button>
          <Button size="lg" variant="destructive" className="w-full" onClick={handleRemoveFromCart}>
            <Trash2 className="mr-2 size-4" />
            {t("cart.button.removeFromCart")}
          </Button>
        </div>
      );
    }

    return (
      <div className={rowWrap}>
        <Button
          variant="brand"
          className={cn(primaryCls, ctaBrand, rowFocus)}
          size={btnSize}
          asChild
        >
          <Link to="/checkout">{label(t("cart.button.goToCheckout"))}</Link>
        </Button>
        <div className={isRow ? "contents" : "flex gap-2"}>
          <Button
            variant={isRow ? "brand-outline" : "destructive"}
            size={btnSize}
            className={cn(isRow && iconOnlyCls, rowFocus)}
            onClick={handleRemoveFromCart}
            aria-label={t("cart.button.removeFromCart")}
          >
            <Trash2 aria-hidden="true" className={isRow ? "size-4" : "size-3.5"} />
          </Button>
          {!isRow && (
            <Button
              variant="brand-outline"
              className={`flex-1 ${outlineCart}`}
              size="sm"
              onClick={() => openCartSidebar(true)}
            >
              <ShoppingCart aria-hidden="true" className="mr-1 size-3.5" />
              {t("cart.button.viewCart")}
            </Button>
          )}
        </div>
      </div>
    );
  }

  // === STATE: Paid course with preview, not enrolled, not in cart ===
  // Antes esto renderizaba CUATRO botones del mismo peso (probar / comprar / agregar
  // preview / agregar compra): parálisis de decisión pura. Son tres intenciones y una es
  // secundaria, así que van con jerarquía: comprar (primario), carrito (secundario) y
  // probar gratis (link). Mismo criterio en card y detail.
  if (isPaid && hasPreview) {
    if (variant === "detail") {
      return (
        <div className="flex flex-col gap-2">
          <Button
            size="lg"
            variant="brand"
            className={`w-full ${ctaBrand}`}
            onClick={() => handleAddAndCheckout(true)}
          >
            {t("cart.button.buyNow")}
          </Button>
          <Button
            size="lg"
            variant="brand-outline"
            className={`w-full ${outlineCart}`}
            onClick={() => handleAddToCart(true)}
          >
            <ShoppingCart className="mr-2 size-4" />
            {t("cart.button.addToCartFull")}
          </Button>
          <button
            type="button"
            onClick={() => handleAddAndCheckout(false)}
            className="mt-1 text-[13.5px] font-bold text-landing-muted underline-offset-4 transition-colors hover:text-landing-red hover:underline"
          >
            {t("cart.button.tryPreview")}
          </button>
        </div>
      );
    }

    return (
      <div className={rowWrap}>
        <Button
          variant="brand"
          className={cn(primaryCls, ctaBrand, rowFocus)}
          size={btnSize}
          onClick={() => handleAddAndCheckout(true)}
        >
          {label(t("cart.button.buy"))}
        </Button>
        <Button
          variant="brand-outline"
          className={cn(iconOnlyCls, outlineCart, rowFocus)}
          size={btnSize}
          onClick={() => handleAddToCart(true)}
          aria-label={cartAria}
        >
          <ShoppingCart aria-hidden="true" className={isRow ? "size-4" : "mr-1 size-3.5"} />
          {srLabel(t("cart.button.addToCartFull"))}
        </Button>
        {/* "Probar gratis las primeras lecciones" no entra en la fila de 44px (son 40 caracteres) y
            no se pierde: la tarjeta ya avisa "Vista previa gratis" en la línea de meta, y el detalle
            del curso —a un click del título— lo ofrece con el mismo peso que siempre. */}
        {!isRow && (
          <button
            type="button"
            onClick={() => handleAddAndCheckout(false)}
            className="text-[12.5px] font-bold text-landing-muted underline-offset-4 transition-colors hover:text-landing-red hover:underline"
          >
            {t("cart.button.tryPreview")}
          </button>
        )}
      </div>
    );
  }

  // === STATE: Paid course without preview, not enrolled, not in cart ===
  if (variant === "detail") {
    return (
      <div className="flex flex-col gap-2">
        <Button
          size="lg"
          variant="brand"
          className={`w-full ${ctaBrand}`}
          onClick={() => handleAddAndCheckout(true)}
        >
          {t("cart.button.buyNow")}
        </Button>
        <Button
          size="lg"
          variant="brand-outline"
          className={`w-full ${outlineCart}`}
          onClick={() => handleAddToCart(true)}
        >
          <ShoppingCart className="mr-2 size-4" />
          {t("cart.button.addToCartFull")}
        </Button>
      </div>
    );
  }

  return (
    <div className={rowWrap}>
      <Button
        variant="brand"
        className={cn(primaryCls, ctaBrand, rowFocus)}
        size={btnSize}
        onClick={() => handleAddAndCheckout(true)}
      >
        {label(t("cart.button.buy"))}
      </Button>
      <Button
        variant="brand-outline"
        className={cn(iconOnlyCls, outlineCart, rowFocus)}
        size={btnSize}
        onClick={() => handleAddToCart(true)}
        aria-label={cartAria}
      >
        <ShoppingCart aria-hidden="true" className={isRow ? "size-4" : "mr-1 size-3.5"} />
        {srLabel(t("cart.button.addToCartFull"))}
      </Button>
    </div>
  );
}
